/**
 * =============================================================================
 * Hook: useReferralAffiliatesInfinite
 * Versão: 1.0.0
 *
 * Descrição:
 *   Variante de scroll infinito (mobile) do listing de afiliados.
 *   Reaproveita 100% a mesma RPC server-side `get_referral_affiliates_paginated`
 *   usada pelo desktop (`useReferralAffiliatesPaginated`), mas via
 *   `useInfiniteQuery` do TanStack Query — cada "scroll até o fim" busca a
 *   próxima página de 20 linhas, sem sobrecarregar o backend.
 *
 *   - Server-side: filtros, ordenação, KPIs e total_count vêm prontos da RPC.
 *   - Achata todas as páginas em uma lista única para renderização contínua.
 *   - QueryKey isolada (`...-infinite`) para não brigar com o cache do desktop.
 *   - `enabled` controlável (a página só ativa este hook no breakpoint mobile).
 *
 * Release notes:
 *   v1.0.0 - Implementação inicial (mobile infinite scroll).
 * =============================================================================
 */
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import type {
  AffiliatesPaginatedFilters,
  AffiliatesKpis,
  PaginatedAffiliate,
} from '@/hooks/useReferralAffiliatesPaginated';

// Logger centralizado para depuração
const logger = createLogger('ReferralAffiliatesInfinite');

const EMPTY_KPIS: AffiliatesKpis = {
  total: 0,
  pending: 0,
  active: 0,
  totalBalance: 0,
};

interface InfinitePage {
  rows: PaginatedAffiliate[];
  totalCount: number;
  kpis: AffiliatesKpis;
  page: number;
}

export interface InfiniteAffiliatesResult {
  affiliates: PaginatedAffiliate[];
  totalCount: number;
  kpis: AffiliatesKpis;
  isLoading: boolean;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  error: Error | null;
  refetch: () => void;
}

interface Options {
  /** Tamanho da página (default 20). */
  pageSize?: number;
  /** Permite desligar o hook quando o usuário não está em mobile. */
  enabled?: boolean;
}

/**
 * Hook de listagem com scroll infinito (mobile) para afiliados.
 *
 * @param filters Filtros aplicados pela RPC (mesma estrutura do desktop).
 * @param opts    Opções: pageSize, enabled.
 */
export function useReferralAffiliatesInfinite(
  filters: AffiliatesPaginatedFilters | undefined,
  opts: Options = {},
): InfiniteAffiliatesResult {
  const { pageSize = 20, enabled = true } = opts;
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  const query = useInfiniteQuery({
    // Sufixo "infinite" isola o cache do hook paginado (desktop)
    queryKey: [
      'referral-affiliates-paginated',
      'infinite',
      currentTenant?.id,
      filters,
      pageSize,
    ],
    initialPageParam: 1,
    queryFn: async ({ pageParam }): Promise<InfinitePage> => {
      const page = Number(pageParam) || 1;
      logger.debug('Buscando página infinite', { page, pageSize, filters });

      // Casting necessário até `types.ts` ser regenerado com a nova RPC
      const { data, error } = await (supabase.rpc as any)(
        'get_referral_affiliates_paginated',
        {
          p_page: page,
          p_page_size: pageSize,
          p_tenant_hint: currentTenant?.id || null,
          p_status: filters?.status || null,
          p_program_id: filters?.program_id || null,
          p_search: filters?.search || null,
          p_only_with_balance: filters?.only_with_balance ?? false,
          p_created_from: filters?.created_from || null,
          p_created_to: filters?.created_to || null,
          p_sort_by: filters?.sort_by || 'created_at',
          p_sort_dir: filters?.sort_dir || 'desc',
        },
      );

      if (error) {
        logger.error('Erro ao buscar página infinite', error);
        throw error;
      }

      // Normaliza memberships/programs_count (RPC pode devolver null)
      const rows = ((data || []) as PaginatedAffiliate[]).map((r) => ({
        ...r,
        memberships: Array.isArray(r.memberships) ? r.memberships : [],
        programs_count: Number(r.programs_count ?? 0),
      }));

      const totalCount = Number(rows[0]?.total_count ?? 0);
      const kpis: AffiliatesKpis = rows.length
        ? {
            total: Number(rows[0]?.kpi_total ?? 0),
            pending: Number(rows[0]?.kpi_pending ?? 0),
            active: Number(rows[0]?.kpi_active ?? 0),
            totalBalance: Number(rows[0]?.kpi_total_balance ?? 0),
          }
        : EMPTY_KPIS;

      return { rows, totalCount, kpis, page };
    },
    /**
     * Avança para a próxima página enquanto ainda há linhas a carregar.
     * Soma o que já está carregado e compara com o total_count global.
     */
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((acc, p) => acc + p.rows.length, 0);
      if (loaded >= lastPage.totalCount) return undefined;
      return lastPage.page + 1;
    },
    enabled: enabled && !!currentTenant?.id,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  // Achata todas as páginas em uma única lista contínua
  const affiliates = useMemo<PaginatedAffiliate[]>(
    () => (query.data?.pages ?? []).flatMap((p) => p.rows),
    [query.data?.pages],
  );

  // KPIs e total vêm da primeira página (são globais, iguais em todas)
  const firstPage = query.data?.pages?.[0];
  const totalCount = firstPage?.totalCount ?? 0;
  const kpis = firstPage?.kpis ?? EMPTY_KPIS;

  return {
    affiliates,
    totalCount,
    kpis,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: !!query.hasNextPage,
    fetchNextPage: () => {
      if (query.hasNextPage && !query.isFetchingNextPage) {
        query.fetchNextPage();
      }
    },
    error: query.error as Error | null,
    refetch: () => {
      // Invalida tanto o paginado quanto o infinite (compartilham prefixo)
      queryClient.invalidateQueries({
        queryKey: ['referral-affiliates-paginated', 'infinite', currentTenant?.id],
      });
    },
  };
}
